import InputRecords from '@/components/common/InputRecords';
import { Descriptions, Empty, Space, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import CopyButton from '@/components/common/CopyButton';
import { detailFields, originSummary } from '@/domain/payeeDetail';
import type { Payee } from '@/api/payees';

const { Text } = Typography;

/**
 * 收款人详情的**正文**，不含抽屉外壳。
 *
 * 抽出来是因为它有两个宿主：收款人页的独立抽屉（PayeeDetailDrawer），以及打款申请 /
 * 打款记录页的记录详情抽屉（PayoutDetailDrawer）里的「收款人」那一段。
 *
 * 🔴 三处必须是**同一份渲染**。同一个收款人在不同页面显示出不同的字段集合，
 * 会让人以为「这条记录上的收款信息和那条不一样」，进而去核对一个根本不存在的差异。
 *
 * 🔴 这里有银行账号。它对本组织成员本来就可见（列表页同源），但**不要**把这些值写进
 * 任何日志或 URL。
 */
export default function PayeeDetailBody({ payee }: { payee: Payee }) {
  const fields = detailFields(payee);

  return (
    <>
      <div style={{ marginBottom: 12 }}><InputRecords key={payee.id} payeeId={payee.id} /></div>
      <Descriptions column={1} size="small" bordered>
        <Descriptions.Item label="短 id">
          <Space size={4}>
            <Text strong copyable={false}>
              {payee.shortId}
            </Text>
            <CopyButton text={payee.shortId} tooltip="复制短 id" successMessage="短 id 已复制" />
          </Space>
        </Descriptions.Item>
        <Descriptions.Item label="别名">{payee.nickname ?? '—'}</Descriptions.Item>
        <Descriptions.Item label="类型">
          {payee.entityType === 'COMPANY' ? '企业' : '个人'}
        </Descriptions.Item>
        <Descriptions.Item label="收款地区 / 币种">
          {payee.bankCountryCode ?? '—'} / {payee.accountCurrency ?? '—'}
        </Descriptions.Item>
        <Descriptions.Item label="汇款方式">
          <Space size={4}>
            <Tag color="blue">{payee.transferMethod ?? '—'}</Tag>
            {payee.localClearingSystem && <Tag>{payee.localClearingSystem}</Tag>}
          </Space>
        </Descriptions.Item>
        <Descriptions.Item label="联系邮箱">
          {payee.contactEmail ? (
            <Space size={4}>
              {payee.contactEmail}
              <CopyButton text={payee.contactEmail} tooltip="复制邮箱" />
            </Space>
          ) : (
            '—'
          )}
        </Descriptions.Item>
        <Descriptions.Item label="来源">
          <Space direction="vertical" size={0}>
            <Space size={4}>
              {payee.origin.kind === 'external' ? (
                <Tag color="orange">对外收集</Tag>
              ) : (
                <Tag>站内添加</Tag>
              )}
              <Text>{originSummary(payee)}</Text>
            </Space>
            {payee.origin.filledAt && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                填写于 {dayjs(payee.origin.filledAt).format('YYYY-MM-DD HH:mm')}
              </Text>
            )}
          </Space>
        </Descriptions.Item>
        <Descriptions.Item label="同步状态">
          {payee.syncStatus === 'SYNCED' ? (
            <Tag color="green">已同步到空中云汇</Tag>
          ) : (
            <Tag>草稿（第一次打款前才推送）</Tag>
          )}
        </Descriptions.Item>
        <Descriptions.Item label="创建时间">
          {dayjs(payee.createdAt).format('YYYY-MM-DD HH:mm')}
        </Descriptions.Item>
      </Descriptions>

      <Typography.Title level={5} style={{ marginTop: 24 }}>
        收款信息
      </Typography.Title>
      {fields.length === 0 ? (
        <Empty description="没有可显示的字段" />
      ) : (
        <Descriptions column={1} size="small" bordered>
          {fields.map((f) => (
            <Descriptions.Item key={f.path} label={f.label}>
              <Space size={4}>
                <Text>{f.value}</Text>
                <CopyButton text={f.value} tooltip={`复制 ${f.label}`} />
              </Space>
            </Descriptions.Item>
          ))}
        </Descriptions>
      )}
    </>
  );
}
